import { Component, OnInit, inject, input, viewChild, output } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef, ColGroupDef } from 'ag-grid-community/dist/lib/entities/colDef';

import { FormlyButtonDropdown, GridRowSelect } from '../../../models';
import { BaseImportService, MenuService } from '../../../services';
import { FormlyService } from '../../../services/component-services/formly.service';
import { BaseInlineFormComponent } from '../../base-inline-form/base-inline-form.component';
import { GridComponent } from '../grid/grid.component';

@Component({
  selector: 'app-tab-grid',
  templateUrl: './tab-grid.component.html',
  styleUrl: './tab-grid.component.scss',
  imports: [BaseInlineFormComponent, GridComponent],
})
export class TabGridComponent implements OnInit {
  private readonly formlyService = inject(FormlyService);
  private readonly menuService = inject(MenuService);

  public readonly grid = viewChild(GridComponent);
  public readonly form = viewChild(BaseInlineFormComponent);

  public readonly stateKey = input<string>();
  public readonly additionalFields = input<FormlyFieldConfig[]>([]);
  public readonly columnDefs = input<(ColDef | ColGroupDef)[]>([]);
  public readonly importService = input<BaseImportService>();
  public readonly importButtonText = input($localize`Import`);
  public readonly dropdownText = input($localize`Download Template`);
  public readonly fields = input<FormlyFieldConfig[]>([]);
  public readonly filter = input(true);
  public readonly gridCssClass = input<string>();
  public readonly header = input<string>();
  public readonly name = input<string>();
  public readonly pagination = input(true);
  public readonly rowData = input<any>([]);
  public readonly multiSelectRows = input(true);
  public readonly showCreate = input(true);
  // Overrides the create button's label - e.g. "Add" on a grid that attaches existing
  // records. Undefined keeps the default "Create".
  public readonly createText = input<string>();
  public readonly showDelete = input(true);
  public readonly showDeleteMessage = input(true);
  public readonly showDiscard = input(true);
  public readonly showExport = input(true);
  public readonly showPrint = input(false);
  public readonly sortable = input(true);
  // ⚠️ The Columns/Filters reset bar shows whenever ANY of filter/sortable/resizable is on,
  // so a small tab grid that wants none of them has to be able to turn this off too - it was
  // the one of the three app-grid never exposed here.
  public readonly resizable = input(true);
  public readonly leftHeader = input(false);
  public readonly fullWidth = input(false);
  public readonly createDropdown = input<FormlyButtonDropdown>();
  public readonly importDropdown = input<FormlyButtonDropdown>();
  public readonly importSelect = input<(file: any) => void>();
  public readonly model = input({});
  public readonly submitText = input('');
  public readonly submitIcon = input('');
  public readonly suppressScrollOnNewData = input(false);
  public readonly create = output<void>();
  public readonly edit = output<any>();
  public readonly delete = output<any>();
  public readonly print = output<any>();
  public readonly rowSelected = output<GridRowSelect>();
  public readonly save = output<void>();
  public readonly imported = output<void>();
  public readonly submittedEvent = output<void>();
  // The grid's sort / filter / column state may have changed (a saved layout applied, or the
  // user sorted, filtered, moved or reset). See GridComponent.stateChanged.
  public readonly stateChanged = output<void>();

  ngOnInit() {
    this.getFields();
  }

  get gridStateKey() {
    return `Tabs.${this.stateKey() ?? this.header() ?? this.name()}`;
  }

  private getFields() {
    if (this.showCreate()) {
      this.fields().push(
        this.formlyService.createButton(
          () => this.create.emit(),
          { dropdown: this.createDropdown() },
          undefined,
          undefined,
          this.createText(),
        ),
      );
    }

    if (this.showExport()) {
      this.fields().push(this.formlyService.exportButton(this.grid(), this.name()));
    }

    if (this.importService() && !this.menuService.isReadOnly) {
      if (this.importDropdown()) {
        this.fields().push(
          this.formlyService.importButton(
            this.importButtonText(),
            (file: any) => {
              if (this.importSelect()) {
                this.importSelect()(file);
              } else {
                this.grid().import(file);
              }
            },
            {
              dropdown: this.importDropdown(),
            },
          ),
        );
      } else {
        this.fields().push(
          this.formlyService.fileUpload(
            this.importButtonText(),
            (file: any) => {
              this.grid().import(file);
              this.imported.emit();
            },
            () => this.grid().exportFile(),
            { dropdownText: this.dropdownText() },
          ),
        );
      }
    }

    if (this.additionalFields()) {
      this.fields().push(...this.additionalFields());
    }
  }
}
